import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Send, Bot, User, Loader2, Plane, Settings, Search
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { User as UserType } from '../types/database';
import toast from 'react-hot-toast';
import SearchPanel from '../components/SearchPanel';

// Available AI models
const AI_MODELS = [
  { id: 'gpt-4o', name: 'GPT-4o', description: 'Latest optimized model' },
  { id: 'gpt-5.2', name: 'GPT-5.2', description: 'Advanced reasoning (if available)' },
] as const;

type ModelId = typeof AI_MODELS[number]['id'];

interface GlobalChatPageProps {
  user: UserType;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export default function GlobalChatPage({ user }: GlobalChatPageProps) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedModel, setSelectedModel] = useState<ModelId>('gpt-4o');
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initial load - set loading to false after a short delay
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    // Add user message immediately
    const tempUserMessage: Message = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      // Call the chat Edge Function directly with anon key to bypass potential auth issues
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/chat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify({
            message: userMessage,
            trip_id: 'global-chat',
            user_id: user.id,
            conversationHistory: messages.map(m => ({
              role: m.role,
              content: m.content
            })),
            tripContext: undefined,
            model: selectedModel,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Chat API error:', response.status, errorText);
        throw new Error(`Chat failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle model access errors
      let assistantContent = data?.response || "I'd love to help you plan an amazing trip!";
      if (data.error && data.requiresUpgrade) {
        assistantContent = `I couldn't use ${AI_MODELS.find(m => m.id === selectedModel)?.name} because: ${data.error}. Please try switching to GPT-4o instead, or upgrade your OpenAI API access at platform.openai.com.`;
        toast.error("Model not available");
      } else if (data.error) {
        assistantContent = `I encountered an issue: ${data.error}`;
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: 'ai-' + Date.now(),
        role: 'assistant',
        content: assistantContent,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      console.error('Error sending message:', err);
      
      // Check if it's an auth error
      if (err.message?.includes('log in') || err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        toast.error('Please log in to use the chat feature');
        navigate('/auth');
        return;
      }
      
      const errorMessage: Message = {
        id: 'error-' + Date.now(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${err.message}. Please try again.`,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-2">
                <Plane className="h-6 w-6 text-primary-600" />
                <div>
                  <h1 className="text-lg font-bold text-gray-900">VoyageAI Chat</h1>
                  <p className="text-sm text-gray-600">AI Travel Assistant</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Model Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowModelSelector(!showModelSelector)}
                  className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  <Settings className="h-4 w-4 text-gray-600" />
                  <span className="text-gray-700">
                    {AI_MODELS.find(m => m.id === selectedModel)?.name}
                  </span>
                </button>
                {showModelSelector && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                      Select AI Model
                    </div>
                    {AI_MODELS.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => {
                          setSelectedModel(model.id);
                          setShowModelSelector(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition ${
                          selectedModel === model.id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${
                            selectedModel === model.id ? 'text-primary-600' : 'text-gray-900'
                          }`}>
                            {model.name}
                          </span>
                          {selectedModel === model.id && (
                            <span className="text-xs text-primary-600">Active</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{model.description}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/dashboard"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Container */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {/* Search Panel Toggle */}
          {showSearchPanel ? (
            <SearchPanel
              tripId="global"
              onClose={() => setShowSearchPanel(false)}
              onSelectItem={(item, type) => {
                setSelectedItems(prev => [...prev, { ...item, type }]);
                toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} selected!`);
              }}
            />
          ) : (
            <button
              onClick={() => setShowSearchPanel(true)}
              className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex items-center justify-center gap-2 text-primary-600"
            >
              <Search className="h-5 w-5" />
              <span className="font-medium">Search Flights, Hotels & Activities</span>
            </button>
          )}

          {/* Selected Items Summary */}
          {selectedItems.length > 0 && (
            <div className="bg-primary-50 rounded-xl p-4 border border-primary-100">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-primary-900">Selected Items ({selectedItems.length})</h4>
                <button
                  onClick={() => setSelectedItems([])}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  Clear all
                </button>
              </div>
              <div className="space-y-2">
                {selectedItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-white rounded-lg p-2 text-sm">
                    <span className="text-gray-900">
                      {item.type === 'flight' && `✈️ ${item.origin} → ${item.destination}`}
                      {item.type === 'hotel' && `🏨 ${item.name || 'Hotel'}`}
                      {item.type === 'activity' && `📍 ${item.name}`}
                    </span>
                    <button
                      onClick={() => setSelectedItems(prev => prev.filter((_, i) => i !== index))}
                      className="text-gray-400 hover:text-red-500"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button className="mt-3 w-full btn-primary text-sm py-2">
                Add to Trip
              </button>
            </div>
          )}

          {/* Welcome Message */}
          {messages.length === 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-gray-900 font-medium mb-2">Hi! I'm your AI Travel Planner 👋</p>
                  <p className="text-gray-600 mb-4">
                    I can help you plan amazing trips, find flights, hotels, and activities.
                    Tell me about your dream vacation!
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setInput("Plan a trip to Paris for 1 week")}
                      className="text-left text-sm bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-lg transition"
                    >
                      🗼 Plan a Paris trip
                    </button>
                    <button
                      onClick={() => setInput("Find beach destinations for summer")}
                      className="text-left text-sm bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-lg transition"
                    >
                      🏖️ Beach destinations
                    </button>
                    <button
                      onClick={() => setInput("Suggest a budget European tour")}
                      className="text-left text-sm bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-lg transition"
                    >
                      💰 Budget European tour
                    </button>
                    <button
                      onClick={() => setInput("What are top honeymoon destinations?")}
                      className="text-left text-sm bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-lg transition"
                    >
                      💑 Honeymoon ideas
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex items-start space-x-2 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' ? 'bg-primary-600' : 'bg-gray-200'
                }`}>
                  {message.role === 'user' ? (
                    <User className="h-4 w-4 text-white" />
                  ) : (
                    <Bot className="h-4 w-4 text-gray-600" />
                  )}
                </div>
                <div className={`rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white shadow-lg'
                }`}>
                  <p className={message.role === 'user' ? 'text-white' : 'text-gray-900'}>
                    {message.content}
                  </p>
                  <p className={`text-xs mt-1 ${message.role === 'user' ? 'text-primary-200' : 'text-gray-400'}`}>
                    {formatTime(message.created_at)}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {sending && (
            <div className="flex justify-start">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <Bot className="h-4 w-4 text-gray-600" />
                </div>
                <div className="bg-white rounded-2xl px-4 py-3 shadow-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <form onSubmit={handleSend} className="flex items-end space-x-2">
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
                placeholder="Ask me about your next adventure..."
                className="input-field resize-none pr-12"
                rows={1}
                disabled={sending}
              />
            </div>
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="btn-primary p-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-5 w-5" />
            </button>
          </form>
          <p className="text-xs text-gray-500 mt-2 text-center">
            💡 Tip: Create a trip to get personalized itinerary suggestions!
          </p>
        </div>
      </div>
    </div>
  );
}
